<template>
  <b-overlay :show="loading" opacity="0.5">
    <reschedule-appointment-modal
      :editing-data="editingData"
      :is-add-active.sync="toggleModal"
      resource="appointments"
      @update="onUpdate"
      @refetchData="fetchAllData"
    />

    <confirm-appointment-modal
      :editing-data="confirmData"
      :is-add-active.sync="toggleConfirmModal"
      resource="appointments"
      @confirm="onConfirmUpdate"
    />

    <start-consultation-modal
      :appointment-data="appointmentData"
      :is-add-active.sync="toggleDoctorModal"
      resource="appointments"
    />

    <add-appointment-modal 
      :is-add-active.sync="toggleAppointMentModal"
      resource="appointments"
      @appointment-created="handleAppointmentCreated"
    />

    <div>
      <list-table
        :records="records" 
        :columns="tableColumns"
        :show-import-button="false"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        :current-page-number.sync="currentPage"
        search-place-holder="Search Patient"
        :show-export-button="can('appointments:CanExport')"
        @export="onExport"
      >

        <template v-if="can('appointments:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="onAddNewAppointment"
          >
            <span class="align-middle text-lg">Add new appointment</span>
          </b-button>
        </template>

        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                class="filter-select"
                placeholder="Status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="statusFilter"
                :options="statusOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => statusFilter = val"
              />
            </b-col>
          </b-row>
        </template>

        <template #cell(appointment_time)="data">
          {{ formatDateTime(data.item.appointment_date) }}
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
            <b-dropdown
              v-if="data.item.status === 'pending' || data.item.status === 'confirmed'"
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item v-if="data.item.status === 'pending'" @click="onConfirm(data.item)">
                <feather-icon icon="CheckIcon" />
                <span class="align-middle ml-50">Confirm</span>
              </b-dropdown-item>
              
              <b-dropdown-item @click="onEdit(data.item)">
                <feather-icon icon="CalendarIcon" />
                <span class="align-middle ml-50">Reschedule</span>
              </b-dropdown-item>
  
              
              <b-dropdown-item v-if="currentUserProfession === 'nurse' || currentUser.id === data.item.medical_professional_id" @click="onBegin(data.item)">
                <feather-icon icon="PlayCircleIcon" />
                <span class="align-middle ml-50">Start Consultation</span>
              </b-dropdown-item>
  
              <b-dropdown-item @click="onAssign(data.item)">
                <feather-icon icon="UserCheckIcon" />
                <span class="align-middle ml-50">Assign to medical professional</span>
              </b-dropdown-item>
  
              <b-dropdown-item v-if="data.item.status === 'pending'" @click="onCancel(data.item)">
                <feather-icon icon="XIcon" class="text-danger" />
                <span class="align-middle ml-50 text-danger">Cancel</span>
              </b-dropdown-item>
            </b-dropdown>
            <span v-else>-</span>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'

import { get, debounce, cloneDeep } from 'lodash';

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddAppointmentModal from '@/@core/components/shared/admin/modals/AddAppointmentModal.vue'
import StartConsultationModal from '@/@core/components/shared/admin/modals/StartConsultationModal.vue'
import RescheduleAppointmentModal from '@/@core/components/shared/admin/modals/RescheduleAppointmentModal.vue'
import ConfirmAppointmentModal from '@/@core/components/shared/admin/modals/ConfirmAppointmentModal.vue'

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BButton,
    BDropdown,
    BFormFile,
    BFormInput,
    BPagination,
    BInputGroup, 
    BDropdownItem,
    BInputGroupAppend, 

    vSelect,
    AddAppointmentModal,
    StartConsultationModal,
    RescheduleAppointmentModal,
    ConfirmAppointmentModal
  },
  directives: {
    Ripple,
  },
  data(){
    return {
      loading: false,
      showFilter: false,
      toggleModal: false,
      toggleConfirmModal: false,
      toggleDoctorModal: false,
      toggleAppointMentModal: false,

      editingData: null,
      confirmData: null,
      searchFilter: null,
      statusFilter: "pending",
      appointmentData: null,

      records: [],

      statusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Completed', value: 'completed' },
        { label: 'Cancelled', value: 'cancelled' },
        { label: 'Confirmed', value: 'confirmed' },
      ],
      
      tableColumns: [
        { key: 'patient.full_name', label: 'Full Name', sortable: false },
        { key: 'patient.partner', label: 'Partner', sortable: false },
        { key: 'appointment_date', label: 'Date', sortable: true, formatter: val => `${this.formatDate(val)}` },
        { key: 'appointment_time', label: 'Time', sortable: false },
        { key: 'status' },
        { key: 'actions' },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    statusFilter: watchHandlerAndResetPageNumber,
    searchFilter: watchHandlerAndResetPageNumber,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);
    this.fetchAllData()

    const { 
      page = 1, 
      search = "", 
      limit = this.perPage,
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = search;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          search: this.searchFilter,
          status: this.statusFilter,
        }

        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.appointments.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.searchFilter,
        }

        const response = await this.useJwt().mainService.appointments.export(query);
        FileDownload(response.data, "appointments.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async onUpdate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to reschedule appointment?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { id, appointment_date, reschedule_reason } = form_data;
        await this.useJwt().mainService.appointments.update(id, { appointment_date, reschedule_reason });
        this.toggleModal = false;

        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Appointment rescheduled successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onConfirmUpdate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to confirm appointment?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { id, appointment_date } = form_data;
        await this.useJwt().mainService.appointments.confirm(id, { appointment_date });
        this.toggleConfirmModal = false;

        await this.fetchAllData();
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Appointment confirmed successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onBegin(appointment){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can('appointments:CanUpdate')) {
          return
        }

        if (!this.can('consultations:CanAddNew')) {
          return
        }

        const response = await this.useJwt().mainService.appointments.startConsultation(appointment.id);
        const id = get(response, 'data.data.id');
        const consultation_data = get(response, 'data.data');

        if (id && consultation_data.consultation_type === 'medical'){
          this.$router.push({
            params: { id },
            name: 'admin-medical-consultation-edit',
          })
        }

        if (id && consultation_data.consultation_type === 'dietary'){
          this.$router.push({
            params: { id },
            name: 'admin-dietary-consultation-edit',
          })
        }
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Consultation started successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onCancel(appointment){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to cancel this appointment?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        await this.useJwt().mainService.appointments.cancel(appointment.id);
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Appointment cancelled successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    onAddNewAppointment(){
      this.toggleAppointMentModal = true;
    },
    onEdit(updating_data){
      this.editingData = cloneDeep(updating_data);
      this.toggleModal = true;
    },
    onConfirm(updating_data){
      this.confirmData = cloneDeep(updating_data);
      this.toggleConfirmModal = true;
    },
    onAssign(appointment){
      this.appointmentData = cloneDeep(appointment);
      this.toggleDoctorModal = true;
    },
    handleAppointmentCreated() {
      this.toggleAppointMentModal = false;
      this.fetchAllData();
    }
    
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }

  .scroll-div { 
    overflow: scroll;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
